/**
 * Payment Links Page — Light + Accent Stripe Design
 * Extension | TMS product tabs, form flow, link generation, history table
 */
import * as React from 'react'
import axios from 'axios'
import { SubscriptionPlan, TmsPaidTier, TmsPlanPrice, TmsTier, TMS_MAX_TRUCKS } from '@numeo/types-core'
import { CircularProgress } from '@mui/joy'
import { Link as LinkIcon, ContentCopy, OpenInNew, Receipt, CheckCircle, Close, DeleteOutline, CreditCard, LocalShipping } from '@mui/icons-material'
import { motion } from 'framer-motion'

const ease = [0.23, 1, 0.32, 1]

type ProductTab = 'extension' | 'tms'

const PRODUCT_TABS: { key: ProductTab; label: string; Icon: typeof CreditCard }[] = [
    { key: 'extension', label: 'Extension', Icon: CreditCard },
    { key: 'tms', label: 'TMS', Icon: LocalShipping },
]

interface PaymentLink {
    _id: string
    managerEmail: string
    product?: ProductTab
    plan?: SubscriptionPlan
    seats?: number
    tmsTier?: TmsPaidTier
    truckCount?: number
    discountPercent: number
    billingInterval: 'month' | 'year'
    intervalCount?: number
    baseAmountCents: number
    finalAmountCents: number
    checkoutUrl: string
    status: 'pending' | 'paid' | 'expired'
    createdBy: string
    createdAt: string
    paidAt?: string
}
interface Product {
    id: string
    name: string
    type: string
    prices: { monthly: { id: string; amount: number } | null; annual: { id: string; amount: number } | null }
}

const DEFAULT_PRICES: Record<string, Record<string, number>> = { PRO: { month: 1499, year: 14990 }, ULTRA: { month: 4999, year: 35988 } }
const NO_DISCOUNT_HINT = 'No discount — the Stripe checkout will show a promo-code field instead.'

export default function PaymentLinksPage() {
    const [activeTab, setActiveTab] = React.useState<ProductTab>('extension')
    const [managerEmail, setManagerEmail] = React.useState('')
    const [plan, setPlan] = React.useState<SubscriptionPlan>(SubscriptionPlan.PRO)
    const [seatsStr, setSeatsStr] = React.useState('1')
    const [tmsTier, setTmsTier] = React.useState<TmsPaidTier>(TmsTier.Basic)
    const [truckCountStr, setTruckCountStr] = React.useState('10')
    // Per-truck rate in DOLLARS — REQUIRED. Auto-fills with the tier's standard rate (and re-fills
    // on tier/interval change); at the standard rate the link uses the standard graduated catalog
    // price (first block included — exact parity with self-serve checkout). A CHANGED rate mints a
    // custom flat price and bills EVERY truck.
    const [truckPriceStr, setTruckPriceStr] = React.useState('')
    const [discountStr, setDiscountStr] = React.useState('0')
    const [billingInterval, setBillingInterval] = React.useState<'month' | 'year'>('month')
    const [generating, setGenerating] = React.useState(false)
    const [generated, setGenerated] = React.useState<{ url: string; finalAmountCents: number } | null>(null)
    const [error, setError] = React.useState<string | null>(null)
    const [emailStatus, setEmailStatus] = React.useState<{ checking: boolean; exists: boolean | null; projectName?: string; errorMessage?: string }>({
        checking: false,
        exists: null,
    })
    const [links, setLinks] = React.useState<PaymentLink[]>([])
    const [loadingLinks, setLoadingLinks] = React.useState(false)
    const [statusFilter, setStatusFilter] = React.useState<string>('all')
    const [prices, setPrices] = React.useState(DEFAULT_PRICES)
    const [products, setProducts] = React.useState<Product[]>([])
    const [tmsCatalog, setTmsCatalog] = React.useState<Partial<Record<TmsTier, TmsPlanPrice | null>> | null>(null)
    const [copied, setCopied] = React.useState<string | null>(null)
    const [deleteConfirm, setDeleteConfirm] = React.useState<string | null>(null)
    const [deleting, setDeleting] = React.useState<string | null>(null)

    const fetchLinks = React.useCallback(async () => {
        setLoadingLinks(true)
        try {
            const params = new URLSearchParams()
            if (statusFilter !== 'all') params.append('status', statusFilter)
            const r = await axios.get(`/gluon/billing/payment-links?${params.toString()}`)
            setLinks(r.data.data)
        } catch {
            // Best-effort refresh: keep the currently displayed links if the fetch fails
        } finally {
            setLoadingLinks(false)
        }
    }, [statusFilter])

    const validateEmail = React.useCallback(async (email: string) => {
        if (!email || !email.includes('@')) {
            setEmailStatus({ checking: false, exists: null })
            return
        }
        setEmailStatus({ checking: true, exists: null })
        try {
            const r = await axios.get(`/gluon/billing/payment-links/validate-email?email=${encodeURIComponent(email)}`)
            setEmailStatus(
                r.data.data.exists
                    ? { checking: false, exists: true, projectName: r.data.data.projectName }
                    : { checking: false, exists: false, errorMessage: r.data.data.message || 'Not found' }
            )
        } catch {
            setEmailStatus({ checking: false, exists: null })
        }
    }, [])

    React.useEffect(() => {
        axios
            .get('/gluon/billing/extension-plans')
            .then((r) => {
                if (r.data?.data?.products) {
                    const prods: Product[] = r.data.data.products
                    setProducts(prods)
                    const np: Record<string, Record<string, number>> = {}
                    for (const p of prods) {
                        np[p.type] = {}
                        if (p.prices.monthly) np[p.type].month = p.prices.monthly.amount
                        if (p.prices.annual) np[p.type].year = p.prices.annual.amount
                    }
                    if (Object.keys(np).length > 0) setPrices((prev) => ({ ...prev, ...np }))
                }
            })
            .catch(() => {})
        axios
            .get('/billing/prices')
            .then((r) => {
                if (r.data?.data?.tms) setTmsCatalog(r.data.data.tms)
            })
            .catch(() => {})
    }, [])

    React.useEffect(() => {
        fetchLinks()
    }, [fetchLinks])

    const switchTab = (tab: ProductTab) => {
        if (tab === activeTab) return
        setActiveTab(tab)
        setManagerEmail('')
        setEmailStatus({ checking: false, exists: null })
        setPlan(SubscriptionPlan.PRO)
        setSeatsStr('1')
        setTmsTier(TmsTier.Basic)
        setTruckCountStr('10')
        setTruckPriceStr('')
        setDiscountStr('0')
        setBillingInterval('month')
        setGenerated(null)
        setError(null)
    }

    const seats = parseInt(seatsStr, 10) || 0
    const truckCount = parseInt(truckCountStr, 10) || 0
    const discountPercent = parseInt(discountStr, 10) || 0

    // Extension pricing — linear: seats × per-seat catalog price (ULTRA included; volume breaks are handled via Discount %)
    const basePricePerSeat = prices[plan]?.[billingInterval] || 0
    const extSubtotal = basePricePerSeat * seats

    // TMS pricing — base + max(0, trucks − included) × extra-truck rate, from the public catalog
    const tierPricing = tmsCatalog?.[tmsTier] ?? null
    const tmsBaseAmount = (billingInterval === 'month' ? tierPricing?.monthly?.amount : tierPricing?.annual?.amount) ?? 0
    const tmsExtraRate = (billingInterval === 'month' ? tierPricing?.extraTruckMonthlyCents : tierPricing?.extraTruckAnnualCents) ?? 0
    // Product default auto-fills (and re-fills on tier/interval/catalog change) — the operator can
    // then override for a negotiated rate. Deliberately overwrites any prior edit on tier switch:
    // a Basic rate carried onto Pro would silently mint a wrong-tier custom price.
    React.useEffect(() => {
        if (tmsExtraRate > 0) setTruckPriceStr((tmsExtraRate / 100).toFixed(2))
    }, [tmsExtraRate])
    const includedTrucks = tierPricing?.includedTrucks ?? 0
    const extraTrucks = Math.max(0, truckCount - includedTrucks)
    const enteredTruckCents = truckPriceStr.trim() === '' ? null : Math.round(Number(truckPriceStr) * 100)
    // REQUIRED: empty is invalid — the field auto-fills with the standard rate, so empty means the
    // operator actively cleared it.
    const truckPriceValid = enteredTruckCents !== null && Number.isFinite(enteredTruckCents) && enteredTruckCents >= 100 && enteredTruckCents <= 50000
    // At the STANDARD rate the link rides the standard graduated catalog price; a CHANGED rate
    // mints a graduated custom price with the SAME included block — either way the first
    // `includedTrucks` are free and only extras bill.
    const isStandardRate = enteredTruckCents !== null && enteredTruckCents === tmsExtraRate
    const customTruckCents = truckPriceValid && !isStandardRate ? enteredTruckCents : null
    const tmsSubtotal = tmsBaseAmount + extraTrucks * (customTruckCents ?? tmsExtraRate)

    const subtotal = activeTab === 'extension' ? extSubtotal : tmsSubtotal

    // Built-in annual discount: the annual catalog price is ALREADY ~30-40% below 12x monthly.
    // Surface it so sales never re-enters the same discount in the Discount field (which would stack).
    const monthlyUnit = activeTab === 'extension' ? (prices[plan]?.month ?? 0) : (tierPricing?.monthly?.amount ?? 0)
    const annualUnit = activeTab === 'extension' ? (prices[plan]?.year ?? 0) : (tierPricing?.annual?.amount ?? 0)
    const annualSavingPct = monthlyUnit > 0 && annualUnit > 0 ? Math.round((1 - annualUnit / (monthlyUnit * 12)) * 100) : 0
    const builtInAnnualPct = billingInterval === 'year' ? annualSavingPct : 0

    const discountAmount = Math.round(subtotal * (discountPercent / 100))
    const finalPrice = subtotal - discountAmount
    const selectedProduct = products.find((p) => p.type === plan)
    const selectedPriceId = selectedProduct?.prices?.[billingInterval === 'month' ? 'monthly' : 'annual']?.id
    const intervalLabel = billingInterval === 'month' ? 'month' : 'year'

    const canGenerate =
        !generating &&
        !!managerEmail &&
        emailStatus.exists === true &&
        (activeTab === 'extension' ? seats >= 1 && seats <= 99 : truckCount >= 1 && truckCount <= TMS_MAX_TRUCKS && !!tierPricing && truckPriceValid)

    const handleGenerate = async () => {
        if (!canGenerate) return
        setGenerating(true)
        setError(null)
        setGenerated(null)
        try {
            const payload =
                activeTab === 'extension'
                    ? { product: 'extension', managerEmail, plan, seats, discountPercent, billingInterval }
                    : {
                          product: 'tms',
                          managerEmail,
                          tmsTier,
                          truckCount,
                          ...(customTruckCents !== null ? { truckPriceCents: customTruckCents } : {}),
                          discountPercent,
                          billingInterval,
                      }
            const r = await axios.post('/gluon/billing/payment-links/generate', payload)
            setGenerated({ url: r.data.data.checkoutUrl, finalAmountCents: r.data.data.finalAmountCents })
            fetchLinks()
        } catch (err) {
            const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined
            setError(message || 'Failed to generate payment link')
        } finally {
            setGenerating(false)
        }
    }

    const copyToClipboard = (url: string, id?: string) => {
        navigator.clipboard.writeText(url)
        setCopied(id || 'generated')
        setTimeout(() => setCopied(null), 2000)
    }

    const handleDelete = async (id: string) => {
        setDeleting(id)
        try {
            await axios.delete(`/gluon/billing/payment-links/${id}`)
            setLinks((prev) => prev.filter((l) => l._id !== id))
        } catch {
            // Delete failed: leave the link in the list so the user can retry
        } finally {
            setDeleting(null)
            setDeleteConfirm(null)
        }
    }

    const emailField = (label: string, placeholder: string) => (
        <div>
            <label className="text-[11px] font-medium text-gray-600 mb-1 block">{label}</label>
            <input
                placeholder={placeholder}
                value={managerEmail}
                onChange={(e) => {
                    setManagerEmail(e.target.value)
                    setEmailStatus({ checking: false, exists: null })
                }}
                onBlur={() => validateEmail(managerEmail)}
                className={`w-full h-8 text-sm px-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${emailStatus.exists === false ? 'border-red-300' : emailStatus.exists === true ? 'border-emerald-300' : 'border-gray-200'}`}
            />
            {emailStatus.checking && <p className="text-[10px] text-gray-400 mt-0.5">Checking...</p>}
            {emailStatus.exists === true && emailStatus.projectName && <p className="text-[10px] text-emerald-600 mt-0.5">Found: {emailStatus.projectName}</p>}
            {emailStatus.exists === false && <p className="text-[10px] text-red-500 mt-0.5">{emailStatus.errorMessage}</p>}
        </div>
    )

    const discountField = (
        <div>
            <label className="text-[11px] font-medium text-gray-600 mb-1 block">Discount</label>
            <div className="flex items-center gap-1">
                <input
                    type="number"
                    value={discountStr}
                    onChange={(e) => setDiscountStr(e.target.value)}
                    onBlur={() => {
                        const n = parseInt(discountStr, 10)
                        if (isNaN(n) || n < 0) setDiscountStr('0')
                        else if (n > 100) setDiscountStr('100')
                    }}
                    className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    min="0"
                    max="100"
                />
                <span className="text-xs font-semibold text-gray-500">%</span>
            </div>
            {builtInAnnualPct > 0 && <p className="text-[10px] text-gray-400 mt-0.5">stacks on top of the built-in {builtInAnnualPct}% annual discount</p>}
            {builtInAnnualPct > 0 && discountPercent >= 25 && (
                <p className="text-[10px] text-amber-600 mt-0.5">⚠ Annual already includes {builtInAnnualPct}% — double-check this isn't the same discount</p>
            )}
        </div>
    )

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span>Home</span>
                <span>/</span>
                <span className="text-gray-600 font-medium">Payment Links</span>
            </div>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-3 mb-6"
            >
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                    <LinkIcon sx={{ fontSize: 18 }} className="text-blue-600" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Payment Links</h1>
                    <p className="text-sm text-gray-500">Generate payment links for company owners to purchase subscriptions</p>
                </div>
            </motion.div>

            {/* Generator Card */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1, ease }}>
                <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-200 overflow-hidden mb-6">
                    <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-5 py-3.5">
                        <h3 className="text-white font-semibold text-sm">Generate Payment Link</h3>
                        <p className="text-slate-300 text-[11px] mt-0.5">Create a Stripe checkout link with custom pricing and discounts</p>
                    </div>
                    <div className="p-4">
                        {/* Product tabs — Extension | TMS */}
                        <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 gap-1 mb-4">
                            {PRODUCT_TABS.map(({ key, label, Icon }) => (
                                <button
                                    key={key}
                                    onClick={() => switchTab(key)}
                                    className={`h-8 px-4 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-all ${activeTab === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    <Icon sx={{ fontSize: 14 }} /> {label}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'extension' ? (
                            <>
                                {/* Row 1: Email + Plan + Interval */}
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    {emailField('Customer Email', 'manager@company.com')}
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Plan</label>
                                        <select
                                            value={plan}
                                            onChange={(e) => setPlan(e.target.value as SubscriptionPlan)}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-white"
                                        >
                                            {products.length > 0 ? (
                                                products.map((p) => (
                                                    <option key={p.type} value={p.type}>
                                                        {p.name}
                                                    </option>
                                                ))
                                            ) : (
                                                <>
                                                    <option value="PRO">Pro</option>
                                                    <option value="ULTRA">Ultra</option>
                                                </>
                                            )}
                                        </select>
                                        {selectedPriceId && <p className="text-[10px] text-gray-400 mt-0.5">Price: {selectedPriceId}</p>}
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Interval</label>
                                        <select
                                            value={billingInterval}
                                            onChange={(e) => setBillingInterval(e.target.value as 'month' | 'year')}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-white"
                                        >
                                            <option value="month">Monthly</option>
                                            <option value="year">Annual</option>
                                        </select>
                                    </div>
                                </div>
                                {/* Row 2: Seats + Price + Discount */}
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Number of Seats</label>
                                        <input
                                            type="number"
                                            value={seatsStr}
                                            onChange={(e) => setSeatsStr(e.target.value)}
                                            onBlur={() => {
                                                const n = parseInt(seatsStr, 10)
                                                if (isNaN(n) || n < 1) setSeatsStr('1')
                                                else if (n > 99) setSeatsStr('99')
                                            }}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                            min="1"
                                            max="99"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Price per Seat</label>
                                        <input
                                            value={`$${(basePricePerSeat / 100).toFixed(2)} / ${intervalLabel}`}
                                            disabled
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-gray-50 font-semibold text-gray-700"
                                        />
                                        {builtInAnnualPct > 0 && (
                                            <p className="text-[10px] text-emerald-600 mt-0.5">
                                                ✓ already {builtInAnnualPct}% off vs monthly (${((monthlyUnit * 12) / 100).toFixed(2)}/yr)
                                            </p>
                                        )}
                                        {billingInterval === 'month' && annualSavingPct > 0 && (
                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                💡 Annual billing saves {annualSavingPct}% (${(annualUnit / 100).toFixed(2)}/yr)
                                            </p>
                                        )}
                                    </div>
                                    {discountField}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Row 1: Email + Plan type + Period */}
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    {emailField('Onboarded Email', 'owner@company.com')}
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Plan Type</label>
                                        <select
                                            value={tmsTier}
                                            onChange={(e) => setTmsTier(e.target.value as TmsPaidTier)}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-white"
                                        >
                                            <option value={TmsTier.Basic}>BASIC</option>
                                            <option value={TmsTier.Pro}>PRO</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Period</label>
                                        <select
                                            value={billingInterval}
                                            onChange={(e) => setBillingInterval(e.target.value as 'month' | 'year')}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-white"
                                        >
                                            <option value="month">Monthly</option>
                                            <option value="year">Annual</option>
                                        </select>
                                    </div>
                                </div>
                                {/* Row 2: Trucks + Base price + Discount */}
                                <div className="grid grid-cols-3 gap-3 mb-3">
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Truck Count</label>
                                        <input
                                            type="number"
                                            value={truckCountStr}
                                            onChange={(e) => setTruckCountStr(e.target.value)}
                                            onBlur={() => {
                                                const n = parseInt(truckCountStr, 10)
                                                if (isNaN(n) || n < 1) setTruckCountStr('1')
                                                else if (n > TMS_MAX_TRUCKS) setTruckCountStr(String(TMS_MAX_TRUCKS))
                                            }}
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                            min="1"
                                            max={TMS_MAX_TRUCKS}
                                        />
                                        {tierPricing && customTruckCents === null && (
                                            <p className="text-[10px] text-gray-400 mt-0.5">First {includedTrucks} trucks included</p>
                                        )}
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 mt-2 block">Truck Price ($/truck, required)</label>
                                        <input
                                            type="number"
                                            value={truckPriceStr}
                                            onChange={(e) => setTruckPriceStr(e.target.value)}
                                            className={`w-full h-8 text-sm px-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${truckPriceValid ? 'border-gray-200' : 'border-red-400'}`}
                                            min="1"
                                            max="500"
                                            step="0.01"
                                        />
                                        {isStandardRate && (
                                            <p className="text-[10px] text-emerald-600 mt-0.5">
                                                Standard rate — first {includedTrucks} trucks included (parity with self-serve)
                                            </p>
                                        )}
                                        {customTruckCents !== null && (
                                            <p className="text-[10px] text-amber-600 mt-0.5">
                                                Custom rate: first {includedTrucks} trucks included, {extraTrucks} extra × $
                                                {(customTruckCents / 100).toFixed(2)} (standard: ${(tmsExtraRate / 100).toFixed(2)})
                                            </p>
                                        )}
                                        {!truckPriceValid && <p className="text-[10px] text-red-500 mt-0.5">Required — enter $1–$500 per truck</p>}
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-medium text-gray-600 mb-1 block">Base Price</label>
                                        <input
                                            value={tierPricing ? `$${(tmsBaseAmount / 100).toFixed(2)} / ${intervalLabel}` : 'Loading...'}
                                            disabled
                                            className="w-full h-8 text-sm px-2 border border-gray-200 rounded-md bg-gray-50 font-semibold text-gray-700"
                                        />
                                        {tierPricing && (
                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                +${(tmsExtraRate / 100).toFixed(2)} / extra truck / {intervalLabel}
                                            </p>
                                        )}
                                        {builtInAnnualPct > 0 && (
                                            <p className="text-[10px] text-emerald-600 mt-0.5">
                                                ✓ already {builtInAnnualPct}% off vs monthly (${((monthlyUnit * 12) / 100).toFixed(2)}/yr)
                                            </p>
                                        )}
                                        {billingInterval === 'month' && annualSavingPct > 0 && (
                                            <p className="text-[10px] text-gray-400 mt-0.5">
                                                💡 Annual billing saves {annualSavingPct}% (${(annualUnit / 100).toFixed(2)}/yr)
                                            </p>
                                        )}
                                    </div>
                                    {discountField}
                                </div>
                            </>
                        )}

                        {/* Price Summary + Generate */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100 bg-gray-50/50 rounded-lg p-3 mt-2">
                            <div>
                                <div className="flex items-baseline gap-1 mb-0.5">
                                    <span className="text-[11px] text-gray-500">Subtotal:</span>
                                    {builtInAnnualPct > 0 && (
                                        <span className="text-[10px] text-emerald-600">(annual price includes {builtInAnnualPct}% off)</span>
                                    )}
                                    <span className="text-sm">
                                        ${(subtotal / 100).toFixed(2)}{' '}
                                        {activeTab === 'extension' ? (
                                            <span className="text-[10px] text-gray-400">
                                                ({seats} seat{seats > 1 ? 's' : ''} x ${(basePricePerSeat / 100).toFixed(2)})
                                            </span>
                                        ) : (
                                            <span className="text-[10px] text-gray-400">
                                                (${(tmsBaseAmount / 100).toFixed(2)} base + {extraTrucks} extra truck{extraTrucks === 1 ? '' : 's'} x $
                                                {(tmsExtraRate / 100).toFixed(2)})
                                            </span>
                                        )}
                                    </span>
                                </div>
                                {discountPercent > 0 ? (
                                    <div className="flex items-baseline gap-1 mb-0.5">
                                        <span className="text-[11px] text-gray-500">Discount:</span>
                                        <span className="text-sm text-emerald-600 font-semibold">
                                            -{discountPercent}% (-${(discountAmount / 100).toFixed(2)})
                                        </span>
                                    </div>
                                ) : (
                                    <p className="text-[10px] text-gray-400 mb-0.5">{NO_DISCOUNT_HINT}</p>
                                )}
                                <div className="flex items-baseline gap-1 mt-1">
                                    <span className="text-xl font-bold text-indigo-700">${(finalPrice / 100).toFixed(2)}</span>
                                    <span className="text-sm text-gray-500">/ {intervalLabel}</span>
                                </div>
                            </div>
                            <button
                                onClick={handleGenerate}
                                disabled={!canGenerate}
                                className="h-9 px-5 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                            >
                                {generating ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '14px' }} /> : <LinkIcon sx={{ fontSize: 16 }} />}
                                {activeTab === 'extension' ? 'Generate Extension Link' : 'Generate TMS Link'}
                            </button>
                        </div>

                        {/* Alerts */}
                        {error && (
                            <div className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm mt-3 flex items-center justify-between">
                                {error}
                                <button onClick={() => setError(null)}>
                                    <Close sx={{ fontSize: 14 }} />
                                </button>
                            </div>
                        )}
                        {generated && (
                            <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg text-sm mt-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <CheckCircle sx={{ fontSize: 16 }} /> Payment link generated! Final total:{' '}
                                    <span className="font-semibold">
                                        ${(generated.finalAmountCents / 100).toFixed(2)} / {intervalLabel}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="text-xs break-all flex-1">{generated.url}</p>
                                    <button
                                        onClick={() => copyToClipboard(generated.url)}
                                        className="w-7 h-7 rounded text-emerald-600 hover:bg-emerald-100 inline-flex items-center justify-center shrink-0"
                                    >
                                        <ContentCopy sx={{ fontSize: 14 }} />
                                    </button>
                                    <a
                                        href={generated.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-7 h-7 rounded text-emerald-600 hover:bg-emerald-100 inline-flex items-center justify-center shrink-0"
                                    >
                                        <OpenInNew sx={{ fontSize: 14 }} />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* History Table */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Receipt sx={{ fontSize: 16 }} className="text-gray-500" />
                            <p className="text-sm font-semibold text-gray-900">Payment Link History</p>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{links.length}</span>
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-8 text-sm px-2 border border-gray-200 rounded-lg bg-white w-28"
                        >
                            <option value="all">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-5 py-3 text-xs font-semibold text-gray-600">Customer Email</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Product</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Plan</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center">Seats / Trucks</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center">Discount</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Final Price</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center">Status</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Created</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center pr-5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingLinks ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12">
                                            <CircularProgress size="sm" />
                                        </td>
                                    </tr>
                                ) : links.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                                            No payment links generated yet
                                        </td>
                                    </tr>
                                ) : (
                                    links.map((link, idx) => {
                                        const isTms = link.product === 'tms'
                                        return (
                                            <motion.tr
                                                key={link._id}
                                                initial={{ opacity: 0, x: -8 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.2, delay: idx * 0.03, ease }}
                                                className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors group"
                                            >
                                                <td className="px-5 py-2.5 font-medium text-gray-800">{link.managerEmail}</td>
                                                <td className="px-2 py-2.5">
                                                    <span
                                                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${isTms ? 'bg-cyan-100 text-cyan-700' : 'bg-blue-100 text-blue-700'}`}
                                                    >
                                                        {isTms ? 'TMS' : 'Extension'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2.5">
                                                    {isTms ? (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyan-100 text-cyan-700">
                                                            {(link.tmsTier || '').toUpperCase()}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${link.plan === 'ULTRA' ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}
                                                        >
                                                            {link.plan}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2.5 text-center text-gray-600">
                                                    {isTms ? `${link.truckCount ?? '—'} trucks` : link.seats}
                                                </td>
                                                <td className="px-2 py-2.5 text-center">
                                                    {link.discountPercent > 0 ? (
                                                        <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                                                            {link.discountPercent}% off
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2.5">
                                                    <span className="font-semibold">${(link.finalAmountCents / 100).toFixed(2)}</span>
                                                    <span className="text-[10px] text-gray-500 ml-0.5">
                                                        /
                                                        {link.intervalCount && link.intervalCount > 1
                                                            ? `${link.intervalCount} ${link.billingInterval}s`
                                                            : link.billingInterval === 'month'
                                                              ? 'mo'
                                                              : 'yr'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2.5 text-center">
                                                    <span
                                                        className={`text-[10px] px-2 py-0.5 rounded ${link.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : link.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
                                                    >
                                                        {link.status}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2.5 text-gray-500 text-xs">
                                                    {new Date(link.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </td>
                                                <td className="px-2 py-2.5 text-center pr-5">
                                                    <div className="inline-flex items-center gap-0.5">
                                                        {link.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => copyToClipboard(link.checkoutUrl, link._id)}
                                                                    className="w-7 h-7 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    title={copied === link._id ? 'Copied!' : 'Copy link'}
                                                                >
                                                                    <ContentCopy sx={{ fontSize: 14 }} />
                                                                </button>
                                                                {deleteConfirm === link._id ? (
                                                                    <span className="flex items-center gap-1 opacity-100">
                                                                        <button
                                                                            onClick={() => handleDelete(link._id)}
                                                                            disabled={deleting === link._id}
                                                                            className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                                                                        >
                                                                            {deleting === link._id ? '...' : 'Yes'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setDeleteConfirm(null)}
                                                                            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
                                                                        >
                                                                            No
                                                                        </button>
                                                                    </span>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => setDeleteConfirm(link._id)}
                                                                        className="w-7 h-7 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                        title="Delete link"
                                                                    >
                                                                        <DeleteOutline sx={{ fontSize: 14 }} />
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                        {link.status === 'paid' && <CheckCircle sx={{ fontSize: 18 }} className="text-emerald-500" />}
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </motion.div>
        </div>
    )
}
